import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  Account,
  BudgetStatus,
  CashFlowMonth,
  Category,
  MonthlySummary,
  Recurrence,
  Transaction,
  TransactionPage,
} from "./types";

/** Todas as chaves de um workspace começam por ["w", wid]: uma mutação invalida o workspace inteiro. */
export const wk = (wid: string, ...rest: unknown[]) => ["w", wid, ...rest] as const;
const base = (wid: string) => `/workspaces/${wid}`;

export function useInvalidateWorkspace(wid: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["w", wid] });
}

/** Mutação que, ao terminar, recarrega os dados do workspace (saldos, listas, relatórios). */
function useWorkspaceMutation<I, O>(wid: string, fn: (input: I) => Promise<O>) {
  const invalidate = useInvalidateWorkspace(wid);
  return useMutation({ mutationFn: fn, onSuccess: () => invalidate() });
}

// ---------- contas ----------
export function useAccounts(wid: string, includeArchived = false) {
  return useQuery({
    queryKey: wk(wid, "accounts", { includeArchived }),
    queryFn: async () =>
      (
        await api.get<{ accounts: Account[] }>(
          `${base(wid)}/accounts${includeArchived ? "?includeArchived=true" : ""}`,
        )
      ).accounts,
  });
}

export function useCreateAccount(wid: string) {
  return useWorkspaceMutation(wid, (body: Record<string, unknown>) =>
    api.post(`${base(wid)}/accounts`, body),
  );
}

export function useUpdateAccount(wid: string) {
  return useWorkspaceMutation(wid, ({ id, ...body }: { id: string; name?: string }) =>
    api.patch(`${base(wid)}/accounts/${id}`, body),
  );
}

export function useArchiveAccount(wid: string) {
  return useWorkspaceMutation(wid, (id: string) => api.post(`${base(wid)}/accounts/${id}/archive`));
}

// ---------- categorias ----------
export function useCategories(wid: string) {
  return useQuery({
    queryKey: wk(wid, "categories"),
    queryFn: async () => (await api.get<{ categories: Category[] }>(`${base(wid)}/categories`)).categories,
  });
}

export function useCreateCategory(wid: string) {
  return useWorkspaceMutation(wid, (body: Record<string, unknown>) =>
    api.post(`${base(wid)}/categories`, body),
  );
}

export function useUpdateCategory(wid: string) {
  return useWorkspaceMutation(wid, ({ id, ...body }: { id: string; name: string }) =>
    api.patch(`${base(wid)}/categories/${id}`, body),
  );
}

export function useDeleteCategory(wid: string) {
  return useWorkspaceMutation(wid, (id: string) =>
    api.delete<{ outcome: "deleted" | "archived" }>(`${base(wid)}/categories/${id}`),
  );
}

// ---------- lançamentos ----------
export type TransactionFilters = Partial<
  Record<"from" | "to" | "accountId" | "categoryId" | "type" | "status" | "tag" | "q", string>
>;

function query(params: Record<string, string | number | undefined>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function useTransactionPages(wid: string, filters: TransactionFilters, limit = 50) {
  return useInfiniteQuery({
    queryKey: wk(wid, "transactions", filters, limit),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<TransactionPage>(`${base(wid)}/transactions${query({ ...filters, limit, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useTransaction(wid: string, id: string | undefined) {
  return useQuery({
    queryKey: wk(wid, "transaction", id),
    enabled: Boolean(id),
    queryFn: async () =>
      (await api.get<{ transaction: Transaction }>(`${base(wid)}/transactions/${id}`)).transaction,
  });
}

/** Corpo do POST mais a `Idempotency-Key` opcional (o "tentar de novo" não duplica, M1-P1). */
type CreateInput = Record<string, unknown> & { idempotencyKey?: string };

export function useCreateTransaction(wid: string) {
  return useWorkspaceMutation(wid, ({ idempotencyKey, ...body }: CreateInput) =>
    api.post(`${base(wid)}/transactions`, body, { idempotencyKey }),
  );
}

export function useUpdateTransaction(wid: string) {
  return useWorkspaceMutation(wid, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`${base(wid)}/transactions/${id}`, body),
  );
}

export function useDeleteTransaction(wid: string) {
  return useWorkspaceMutation(wid, (id: string) => api.delete(`${base(wid)}/transactions/${id}`));
}

export function useCreateTransfer(wid: string) {
  return useWorkspaceMutation(wid, ({ idempotencyKey, ...body }: CreateInput) =>
    api.post(`${base(wid)}/transfers`, body, { idempotencyKey }),
  );
}

// ---------- relatórios e orçamentos ----------
export function useMonthlySummary(wid: string, month: string) {
  return useQuery({
    queryKey: wk(wid, "summary", month),
    queryFn: () => api.get<MonthlySummary>(`${base(wid)}/reports/monthly-summary?month=${month}`),
  });
}

export function useCashFlow(wid: string, from: string, to: string) {
  return useQuery({
    queryKey: wk(wid, "cash-flow", from, to),
    queryFn: async () =>
      (await api.get<{ months: CashFlowMonth[] }>(`${base(wid)}/reports/cash-flow?from=${from}&to=${to}`))
        .months,
  });
}

export function useBudgetStatus(wid: string, month: string) {
  return useQuery({
    queryKey: wk(wid, "budget-status", month),
    queryFn: async () =>
      (await api.get<{ budgets: BudgetStatus[] }>(`${base(wid)}/budgets/status?month=${month}`)).budgets,
  });
}

export function useSetBudget(wid: string) {
  return useWorkspaceMutation(
    wid,
    ({ categoryId, monthlyLimitMinor }: { categoryId: string; monthlyLimitMinor: number }) =>
      api.put(`${base(wid)}/budgets/${categoryId}`, { monthlyLimitMinor }),
  );
}

export function useDeleteBudget(wid: string) {
  return useWorkspaceMutation(wid, (categoryId: string) => api.delete(`${base(wid)}/budgets/${categoryId}`));
}

// ---------- recorrências ----------
export function useRecurrences(wid: string) {
  return useQuery({
    queryKey: wk(wid, "recurrences"),
    queryFn: async () =>
      (await api.get<{ recurrences: Recurrence[] }>(`${base(wid)}/recurrences`)).recurrences,
  });
}

export function useCreateRecurrence(wid: string) {
  return useWorkspaceMutation(wid, (body: Record<string, unknown>) =>
    api.post(`${base(wid)}/recurrences`, body),
  );
}

export function useDeleteRecurrence(wid: string) {
  return useWorkspaceMutation(wid, (id: string) => api.delete(`${base(wid)}/recurrences/${id}`));
}
